import React, { useState, useEffect } from "react";
import { Editor } from "@tinymce/tinymce-react";
import axios from "../api/axios";
import { useAuth } from "../context/AuthContext";
import styles from "./AboutUs.module.css";
import { useParams } from "react-router-dom";

const tinymceApiKey = process.env.REACT_APP_TINYMCE_API_KEY || '';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5069';

interface PageTemplateProps {
  pageName?: string;  // optional to allow dynamic usage
  pageTitle?: string;
}

const PageTemplate: React.FC<PageTemplateProps> = ({ pageName, pageTitle }) => {
  const { name } = useParams();
  const actualPageName = (pageName || name || "Unknown").trim();
  const actualPageTitle = pageTitle || actualPageName;

  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";

  const [content, setContent] = useState<string>("Loading...");
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [tempContent, setTempContent] = useState<string>("");

  useEffect(() => {
    axios
      .get(`/pages/${actualPageName}`)
      .then((response) => {
        setContent(response.data.content);
      })
      .catch((error) => {
        console.error("Error fetching page content:", error);
        setContent("Failed to load content.");
      });
  }, [actualPageName]);

  const startEditing = () => {
    setTempContent(content);
    setIsEditing(true);
  };

  const saveContent = () => {
    axios
      .put(`/pages/${actualPageName}`, { content: tempContent })
      .then((res) => {
        console.log("Content updated:", res.data);
        setContent(tempContent);
        setIsEditing(false);
      })
      .catch((err) => {
        console.error("Error updating content:", err);
        alert("Failed to update content.");
      });
  };

  const cancelEditing = () => {
    setIsEditing(false);
  };

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.content}>
        <h1>{actualPageTitle}</h1>
        {isEditing ? (
          <>
            <Editor
            apiKey={tinymceApiKey}
            value={tempContent}
            onEditorChange={(newContent) => setTempContent(newContent)}
            init={{
                height: 300,
                menubar: true,
                plugins: [
                "advlist", "autolink", "lists", "link", "image", "charmap", "preview",
                "anchor", "searchreplace", "visualblocks", "code", "fullscreen",
                "insertdatetime", "media", "table", "help", "wordcount"
                ],
                toolbar:
                "undo redo | formatselect | bold italic underline forecolor | " +
                "alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image media | removeformat | help",
                automatic_uploads: true,
                images_upload_url: `${API_URL}/api/upload/image`, // ✅ point to your backend upload endpoint
                file_picker_types: "image",
                file_picker_callback: function (
                    callback: (url: string, meta?: object) => void,
                    value: string,
                    meta: { filetype: string }
                  ) {
                    if (meta.filetype === "image") {
                      const input = document.createElement("input");
                      input.setAttribute("type", "file");
                      input.setAttribute("accept", "image/*");
                      input.onchange = async function () {
                        const file = input.files?.[0];
                        if (!file) return;
                  
                        const formData = new FormData();
                        formData.append("image", file);
                  
                        try {
                          const response = await fetch(`${API_URL}/api/upload/image`, {
                            method: "POST",
                            body: formData,
                          });
                  
                          const result = await response.json();
                          callback(result.imageUrl); // Image inserted into editor
                        } catch (error) {
                          console.error("Image upload failed:", error);
                        }
                      };
                      input.click();
                    }
                  },
                images_reuse_filename: true,
                media_live_embeds: true,
                extended_valid_elements: "iframe[src|frameborder|style|scrolling|class|width|height|name|align]",
            }}
            />
            <div style={{ marginTop: "10px" }}>
              <button onClick={saveContent} style={{ marginRight: "10px" }}>Save</button>
              <button onClick={cancelEditing}>Cancel</button>
            </div>
          </>
        ) : (
          <div dangerouslySetInnerHTML={{ __html: content }} />
        )}
        {isAdmin && !isEditing && (
          <button onClick={startEditing} style={{ marginTop: "10px" }}>
            Edit
          </button>
        )}
      </div>
    </div>
  );
};

export default PageTemplate;
